import React from "react";
import {
    CustomStyleFormHelperText,
} from "../Styles";
import { DECIMAL_REGEX } from "../../Apis/functions";
import {useTranslation} from "react-i18next";
import Cookies from "js-cookie";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";


/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 * @Description Custom hook to handle text field behavior, styles, validation and character counting.
 *
 * @param {string} type Input type (e.g. "text", "number").
 * @param {number|string} length Maximum allowed length, use "MAX" for unlimited.
 * @param {string|number} height Custom height for the input element.
 * @param {string} backgroundColor Background color for the input.
 * @param {string} fontColor Font color for the input text.
 * @param {Object} inputProps Additional input properties passed to the component.
 * @param {boolean} countDecimals Enables decimal validation for numeric inputs.
 * @param {Function} onKeyDown Custom key down handler.
 * @param {Function} onChange Change handler for input value updates.
 * @param {boolean} multiline Indicates whether the field is a textarea.
 * @param value
 */
const CustomTextFieldApp = ({
    type,
    length,
    height,
    backgroundColor,
    fontColor,
    inputProps,
    countDecimals,
    decimals,
    onKeyDown,
    onChange,
    value,
    multiline
}) => {
    const { t } = useTranslation();
    const lCode = Cookies.get('i18next') || 'en';
    const charsList = ['e', 'E', '-', '+'];
    const valueEmpty = value === null || value === '';

    let style = {
        backgroundColor: backgroundColor,
        borderRadius: "4px",
        width: "100%"
    };

    if (fontColor) {
        style = { ...style, color: fontColor, '-webkit-text-fill-color': fontColor };
    }

    if (height) {
        style = { ...style, height: height };
    }

    if (decimals != null) {
        inputProps = { ...inputProps, pattern: DECIMAL_REGEX.source };
    }

    if (length !== "MAX") {
        inputProps = { ...inputProps, maxLength: decimals != null ? length + 1 + decimals : length };
    }

    /**
     * Length a value is read with when it is sent.
     *
     * A payload writes every line break of a textarea as CRLF, so a break
     * travels as two characters while the field counts it as one; the value is
     * measured the way the service reads it, so what is written always fits in
     * the field it is stored in.
     *
     * @param {*} text Value being measured.
     *
     * @returns {number} Length of the value as it is sent.
     */
    const getSentLength = (text) => String(text ?? '').replace(/\r?\n/g, '\r\n').length;

    /**
     * Cuts a value down to what fits in the field once it is sent.
     *
     * @param {*} text Value being written.
     *
     * @returns {string} Value that fits in the field.
     */
    const clampToSentLength = (text) => {
        let result = String(text ?? '');

        while (result.length > 0 && getSentLength(result) > length)
            result = result.slice(0, -1);

        return result;
    };

    /**
     * Handles the change of a textarea, limited by the length it is sent with.
     *
     * The maximum length of the field counts a line break as one character, so
     * the value is measured again before it is written and what does not fit is
     * cut instead of refused, because a text that is pasted whole is written
     * with as much of it as the field holds.
     *
     * @param {React.ChangeEvent<HTMLTextAreaElement>} e Input change event.
     */
    const handleMultilineChange = (e) => {
        const clamped = clampToSentLength(e.target.value);

        if (clamped === e.target.value) return onChange(e);

        onChange({target: {name: e.target.name, value: clamped}});
    };

    /**
     * Handles numeric input change validation.
     * Prevents invalid characters and enforces decimal and length restrictions.
     *
     * @param {React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>} e Input change event.
     */
    const handleNumberInputChange = (e) => {
        if (charsList.includes(e?.nativeEvent?.data) && type === "number") {
            e.preventDefault();
            return;
        }
        const newValue = e.target.value;
        const regex = countDecimals ? /^\d*\.?\d{0,1}$/ : /^\d*$/;
        if (regex.test(newValue) && newValue.length <= length) {
            onChange(e);
        }
    };

    /**
     * Handles key down events.
     * Prevents invalid characters for numeric inputs and delegates to custom handler if provided.
     *
     * @param {React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>} e Key down event.
     */
    const handleKeyDown = (e) => {
        if (charsList.includes(e.key) && type === "number") {
            e.preventDefault();
            return;
        }
        if (e.key === '.' && type === "number" && decimals == null) {
            e.preventDefault();
            return;
        }
        if (decimals != null && type !== "number") {
            const controlKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Home', 'End'];
            if (controlKeys.includes(e.key) || e.ctrlKey || e.metaKey) {
                // allow
            } else if (e.key === '.') {
                const { value, selectionStart } = e.target;
                const hasSelection = selectionStart !== e.target.selectionEnd;
                if (value.includes('.') || (!hasSelection && value.length > length)) {
                    e.preventDefault();
                    return;
                }
            } else if (/^\d$/.test(e.key)) {
                const { value, selectionStart } = e.target;
                const hasSelection = selectionStart !== e.target.selectionEnd;
                if (!hasSelection) {
                    const dotIndex = value.indexOf('.');
                    if (dotIndex === -1 && value.length >= length) {
                        e.preventDefault();
                        return;
                    }
                    if (dotIndex !== -1 && selectionStart > dotIndex && value.substring(dotIndex + 1).length >= decimals) {
                        e.preventDefault();
                        return;
                    }
                }
            } else {
                e.preventDefault();
                return;
            }
        }
        if (onKeyDown) {
            onKeyDown(e);
        }
    };

    /**
     * Handles paste events.
     * Validates pasted content to prevent invalid characters in numeric inputs.
     *
     * @param {React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>} e Paste event.
     */
    const handlePaste = (e) => {
        const pasteData = e.clipboardData.getData('text');
        let isValid = true;

        for (let i = 0; i < pasteData.length; i++) {
            if (charsList.includes(pasteData[i]) && type === "number") {
                isValid = false;
                break;
            }
        }

        if (!isValid) {
            e.preventDefault();
            return;
        }
    }

    /**
     * Renders a character counter helper component.
     *
     * @param {Object} props CharacterCount props.
     * @param {number} props.count Current character count.
     * @param {string} props.helperText Helper text displayed on the left.
     * @param {number} props.max Maximum allowed characters.
     * @param {boolean} props.disabled Indicates whether the counter is disabled.
     * @param {string} props.counterColor Color for the counter text.
     * @param {boolean} props.showCounter Controls visibility of the counter.
     * @param {string} props.errorClass CSS class applied when error state is active.
     *
     * @returns {JSX.Element} Character counter component.
     */
    const CharacterCount = ({ count, helperText, max, disabled, counterColor, showCounter, errorClass , marginBottom}) => {
        return showCounter ? <Stack
            component="span"
            className={`${errorClass} w-100`}
            direction="row"
            justifyContent={helperText ? 'space-between' : 'flex-end'}
            whiteSpace={helperText ? "normal" : "nowrap"}
            sx={CustomStyleFormHelperText(showCounter, marginBottom, false)}
            disabled={disabled}
        >
            {showCounter && <>
                <Box component="span" overflow="hidden" textOverflow="ellipsis">
                    {helperText}
                </Box>

                {!helperText && (!disabled ? (
                    <Box className={"counter-numbers"} component="span"
                         color={count >= max ? 'error.main' : counterColor}>
                        {count} / {max}
                    </Box>
                ) : (
                    <Box component="span" className={"mb-1rem"}/>
                ))}
            </>}
        </Stack>
            : null;

    };


    return {
        t,
        style,
        customInputProps: inputProps,
        lCode,
        valueEmpty,
        handleKeyDown,
        handleNumberInputChange,
        handleMultilineChange,
        handlePaste,
        getSentLength,
        CharacterCount
    };
};

export default CustomTextFieldApp;
